<template>
  <div
    ref="contentWrapper"
    class="el-switch"
    :class="[{ 'is-disabled': switchDisabled, 'is-checked': checked }]"
    :aria-checked="checked"
    :aria-disabled="switchDisabled"
    :style="rtBaseSwitchWrapperStyle"
    @click.prevent="switchValue"
  >
    <input
      :id="id"
      ref="input"
      class="el-switch__input"
      type="checkbox"
      :name="name"
      :true-value="activeValue"
      :false-value="inactiveValue"
      :disabled="switchDisabled"
      @change="handleChange"
      @keydown.enter="switchValue"
    >
    <div
      class="el-switch__core"
      :class="['content-wrapper', isAnimationFinished && 'content-wrapper-animation', 'rt-switch-' + size]"
      :style="{ 'width': coreWidth + 'px', left: contentWrapperLeft }"
    >
      <div
        class="active-wrapper"
        :style="{width: computedActiveTextWidth + 'px'}"
      >
        <span
          ref="activeText"
          class="active-text"
        >{{ activeText }}</span>
      </div>
      <div
        class="circle"
        :style="circleStyle"
      >
        <rt-icon
          v-if="computedIconName"
          :name="computedIconName"
          :color="computedIconColor"
        />
      </div>
      <div
        class="inactive-wrapper"
        :style="{width: computedInactiveTextWidth + 'px'}"
      >
        <span
          ref="inactiveText"
          class="inactive-text"
        >{{ inactiveText }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import emitter from 'element-ui/lib/mixins/emitter';
import Focus from 'element-ui/lib/mixins/focus';
import Migrating from 'element-ui/lib/mixins/migrating';
import RtIcon from '../../../../components/icon/rtIcon';

export default {
  name: 'ElSwitch',
  components: { RtIcon },
  mixins: [Focus('input'), Migrating, emitter],
  inject: {
    elForm: {
      default: ''
    }
  },
  props: {
    value: {
      type: [Boolean, String, Number],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    width: {
      type: [Number, String],
      default: ''
    },
    /** * @public 组件容器的高度,默认根据按钮尺寸自适应,用户可以自定义 */
    height: {
      type: [Number, String],
      default: ''
    },
    /** * @public 按钮宽度 */
    circleWidth: {
      type: [Number, String],
      default: ''
    },
    activeIconClass: {
      type: String,
      default: ''
    },
    inactiveIconClass: {
      type: String,
      default: ''
    },
    activeText: String,
    inactiveText: String,
    activeColor: {
      type: String,
      default: function () {
        return this.SCSS_VARIABLES.COLOR_PRIMARY;
      }
    },
    inactiveColor: {
      type: String,
      default: function () {
        return this.SCSS_VARIABLES.NEUTRAL_COLOR_SECONDARY;
      }
    },
    activeValue: {
      type: [Boolean, String, Number],
      default: true
    },
    inactiveValue: {
      type: [Boolean, String, Number],
      default: false
    },
    name: {
      type: String,
      default: ''
    },
    validateEvent: {
      type: Boolean,
      default: true
    },
    id: String,
    size: {
      type: String,
      default: 'large'
    }
  },
  data () {
    return {
      isAnimationFinished: false,
      // 左右内容容器的宽度
      activeTextWidth: 0,
      inactiveTextWidth: 0
    };
  },
  computed: {
    checked () {
      return this.value === this.activeValue;
    },
    switchDisabled () {
      return this.disabled || (this.elForm || {}).disabled;
    },
    // 主元素内边距
    computedPadding () {
      let padding = 2;
      if (this.height && this.actualCircleWidth >= Number(this.height)) {
        padding = 0;
      }
      return padding;
    },
    // 可视容器的内联样式
    rtBaseSwitchWrapperStyle () {
      let width, height;
      width = this.computedActiveTextWidth + this.actualCircleWidth + 2 * this.computedPadding;
      height = this.height ? Number(this.height) : this.actualCircleWidth + 2 * this.computedPadding;
      return {
        width: width + 'px',
        height: height + 'px',
        backgroundColor: this.checked ? this.activeColor : this.inactiveColor,
        padding: this.computedPadding + 'px'
      };
    },
    // 左侧内容宽度
    computedActiveTextWidth () {
      let width; let totalWidth;
      if (this.activeTextWidth) {
        width = this.activeTextWidth
      } else {
        totalWidth = this.width ? Number(this.width) : this.size === 'large' ? 48 : 40;
        width = totalWidth - 2 * this.computedPadding - this.actualCircleWidth;
      }
      return width;
    },
    // 右侧内容宽度
    computedInactiveTextWidth () {
      return this.inactiveText ? this.inactiveTextWidth : this.computedActiveTextWidth;
    },
    // 自定义按钮宽高
    actualCircleWidth () {
      let circleWidth = this.size === 'large' ? 20 : 16;
      if (this.circleWidth) {
        circleWidth = Number(this.circleWidth);
      }
      return circleWidth;
    },
    circleStyle () {
      return {
        width: this.actualCircleWidth + 'px',
        height: this.actualCircleWidth + 'px'
      };
    },
    // 不同状态下的按钮图标名称
    computedIconName () {
      return this.checked ? this.activeIconClass : this.inactiveIconClass;
    },
    computedIconColor () {
      return this.checked ? this.activeColor : this.inactiveColor;
    },
    // 内容容器的left值,动画需要
    contentWrapperLeft () {
      if (this.checked) {
        return '0px';
      } else {
        return `-${this.computedActiveTextWidth}px`;
      }
    },
    // 内容和按钮总区域宽度
    coreWidth () {
      return this.computedActiveTextWidth + this.computedInactiveTextWidth + this.actualCircleWidth;
    }
  },
  watch: {
    checked () {
      this.$refs.input.checked = this.checked;
      if (this.validateEvent) {
        this.dispatch('ElFormItem', 'el.form.change', [this.value]);
      }
    }
  },
  created () {
    if (!~[this.activeValue, this.inactiveValue].indexOf(this.value)) {
      this.$emit('input', this.inactiveValue);
    }
  },
  mounted () {
    this.init();
    if (typeof ResizeObserver === 'function') {
      this.observer = new ResizeObserver(() => {
        this.init();
      });
      this.observer.observe(this.$refs.contentWrapper);
    }
  },
  beforeDestroy () {
    // 移除容器尺寸变化的监听函数
    this.observer && this.observer.unobserve(this.$refs.contentWrapper);
  },
  methods: {
    init () {
      this.$refs.input.checked = this.checked;
      this.$nextTick(() => {
        this.activeTextWidth = this.activeText ? this.$refs.activeText && this.$refs.activeText.getBoundingClientRect().width : 0;
        this.inactiveTextWidth = this.$refs.inactiveText && this.$refs.inactiveText.getBoundingClientRect().width;
      });
      this.$setTimeout(() => {
        // left 过渡动画时长为200毫秒，需要预留这个时间，防止初始值出现过渡效果
        this.isAnimationFinished = true;
      }, 200);
    },
    handleChange (event) {
      const val = this.checked ? this.inactiveValue : this.activeValue;
      this.$emit('input', val);
      this.$emit('change', val);
      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.checked = this.checked;
        }
      });
    },
    switchValue () {
      !this.switchDisabled && this.handleChange();
    }
  }
};
</script>



// WEBPACK FOOTER //
// component.vue?265cbb92